
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;

        this.form.addEventListener(
          'submit',
          this.onSubmitHandler.bind(this)
        );

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      // MAIN SUBMIT HANDLER
      onSubmitHandler(evt) {
        evt.preventDefault();

        if (
          this.submitButton.getAttribute('aria-disabled') === 'true'
        ) return;

        const coverVariantId = Number(
          this.dataset.promoCoverVariantId
        );

        // PJ products with matching cover
        if (
          Number.isSafeInteger(coverVariantId) &&
          coverVariantId > 0
        ) {
          this.addPjWithCover(coverVariantId);
          return;
        }

        // Other products use normal Dawn functionality
        this.addRegularProduct();
      }

      // DEFAULT DAWN ADD TO CART
      async addRegularProduct() {
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');

        const spinner = this.querySelector('.loading__spinner');
        spinner?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map(
              section => section.id
            )
          );

          formData.append(
            'sections_url',
            window.location.pathname
          );

          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        try {
          const response = await fetch(
            routes.cart_add_url,
            config
          );

          const data = await response.json();

          if (data.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: data.errors || data.description,
              message: data.message
            });

            this.handleErrorMessage(
              data.description || data.message
            );

            const soldOutMessage = this.submitButton.querySelector(
              '.sold-out-message'
            );

            this.error = true;

            if (soldOutMessage) {
              this.submitButton.setAttribute(
                'aria-disabled',
                'true'
              );

              this.submitButton.querySelector('span')
                ?.classList.add('hidden');

              soldOutMessage.classList.remove('hidden');
            }

            return;
          }

          this.error = false;

          if (!this.cart) {
            window.location.assign(window.routes.cart_url);
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: data
          });

          const quickAddModal = this.closest(
            'quick-add-modal'
          );

          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => {
                  this.cart.renderContents(data);
                });
              },
              { once: true }
            );

            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(data);
          }

        } catch (error) {
          console.error('Cart error:', error);
          this.handleErrorMessage(
            error.message || 'Unable to add product.'
          );
        } finally {
          this.submitButton.classList.remove('loading');

          if (
            this.cart &&
            this.cart.classList.contains('is-empty')
          ) {
            this.cart.classList.remove('is-empty');
          }

          if (!this.error) {
            this.submitButton.removeAttribute('aria-disabled');
          }

          spinner?.classList.add('hidden');
        }
      }

      // PJ + MATCHING COVER AUTO ADD
      async addPjWithCover(coverVariantId) {
        this.handleErrorMessage();

        const button = this.submitButton;
        const spinner = this.querySelector('.loading__spinner');

        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        spinner?.classList.remove('hidden');

        try {
          const formData = new FormData(this.form);

          const mainVariantId = Number(formData.get('id'));

          const quantity = Number.parseInt(
            formData.get('quantity') || '1',
            10
          );

          if (
            !Number.isSafeInteger(mainVariantId) ||
            mainVariantId <= 0
          ) {
            throw new Error('Invalid PJ variant.');
          }

          if (
            !Number.isSafeInteger(quantity) ||
            quantity < 1
          ) {
            throw new Error('Invalid quantity.');
          }

          const mainItem = {
            id: mainVariantId,
            quantity: quantity
          };

          // Preserve product properties
          const properties = {};

          for (const [key, value] of formData.entries()) {
            const match = key.match(/^properties\[(.+)\]$/);

            if (!match) continue;

            if (value instanceof File) {
              if (value.size > 0) {
                throw new Error(
                  'File upload properties require separate handling.'
                );
              }
              continue;
            }

            properties[match[1]] = value;
          }

          if (Object.keys(properties).length) {
            mainItem.properties = properties;
          }

          // Preserve selling plan
          const sellingPlan = formData.get('selling_plan');

          if (sellingPlan) {
            mainItem.selling_plan = Number(sellingPlan);
          }

          // One automatic cover for each PJ unit
          const coverItem = {
            id: coverVariantId,
            quantity: quantity,
            properties: {
              _pb_auto_cover: 'true',
              _pb_parent_variant: String(mainVariantId)
            }
          };

          const root = window.Shopify?.routes?.root || '/';

          const response = await fetch(
            root + 'cart/add.js',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              },
              body: JSON.stringify({
                items: [mainItem, coverItem]
              })
            }
          );

          const result = await response.json();

          if (!response.ok || result.status) {
            throw new Error(
              result.description ||
              result.message ||
              'Unable to add PJ and cover.'
            );
          }

          // Refresh cart to show both items
          window.location.assign(root + 'cart');

        } catch (error) {
          console.error('PJ Auto Cover Error:', error);

          this.handleErrorMessage(
            error.message || 'Unable to add products.'
          );

          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
          spinner?.classList.add('hidden');
        }
      }

      // DEFAULT DAWN ERROR HANDLING
      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}